import React from 'react';
import {
  Box,
  ListItem,
  Typography,
  Paper,
  Chip,
  Card,
  CardContent,
} from '@mui/material';

const formatCurrency = (num) => {
  if (num === null || num === undefined) return '-';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(num);
};

const formatNumber = (num, decimals = 4) => {
  if (num === null || num === undefined) return '-';
  return Number(num).toLocaleString(undefined, { maximumFractionDigits: decimals });
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    return new Date(dateString).toLocaleString();
  } catch {
    return dateString;
  }
};

const MetricRow = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="body2" sx={{ fontWeight: 500 }}>{value}</Typography>
  </Box>
);

const NotificationHyperliquidPositionsCard = ({ hit }) => {
  const pos = hit._source || {};
  const wallet = pos.wallet_address || (hit._id || '').split('_')[0];
  const token = pos.token_address || (hit._id || '').split('_').slice(1).join('_');

  return (
    <ListItem
      sx={{
        borderBottom: '1px solid #e0e0e0',
        flexDirection: 'column',
        alignItems: 'stretch',
        py: 2,
        px: 0,
      }}
    >
      <Card elevation={1} sx={{ width: '100%', backgroundColor: '#fafafa', overflow: 'visible' }}>
        <CardContent sx={{ pb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                {token || 'Unknown Token'}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', wordBreak: 'break-all', fontSize: '0.75rem' }}>
                {wallet}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                {pos.chain && <Chip size="small" label={pos.chain} variant="outlined" />}
                <Chip
                  size="small"
                  label={pos.is_closed ? 'closed' : 'open'}
                  color={pos.is_closed ? 'default' : 'success'}
                  variant="outlined"
                />
              </Box>
            </Box>
            <Chip label={hit._score != null ? hit._score.toFixed(2) : '-'} size="small" variant="outlined" color="primary" />
          </Box>

          <Paper elevation={0} sx={{ p: 1.5, mb: 2, backgroundColor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Position</Typography>
            <MetricRow label="Realized PnL" value={formatCurrency(pos.realized_pnl_usd)} />
            <MetricRow label="Avg Cost Basis" value={formatCurrency(pos.avg_cost_basis_usd)} />
            <MetricRow label="Remaining Qty" value={formatNumber(pos.remaining_qty)} />
          </Paper>

          <Paper elevation={0} sx={{ p: 1.5, mb: 2, backgroundColor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Volume</Typography>
            <MetricRow label="Total Bought (USD)" value={formatCurrency(pos.total_bought_usd)} />
            <MetricRow label="Total Sold (USD)" value={formatCurrency(pos.total_sold_usd)} />
            <MetricRow label="Total Bought (qty)" value={formatNumber(pos.total_bought_qty)} />
            <MetricRow label="Total Sold (qty)" value={formatNumber(pos.total_sold_qty)} />
            <MetricRow label="Buys / Sells" value={`${formatNumber(pos.buy_count, 0)} / ${formatNumber(pos.sell_count, 0)}`} />
          </Paper>

          <Paper elevation={0} sx={{ p: 1.5, backgroundColor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Timestamps</Typography>
            <MetricRow label="First Trade" value={formatDate(pos.first_trade_time)} />
            <MetricRow label="Last Trade" value={formatDate(pos.last_trade_time)} />
            <MetricRow label="Updated" value={formatDate(pos.updated_at)} />
          </Paper>
        </CardContent>
      </Card>
    </ListItem>
  );
};

export default NotificationHyperliquidPositionsCard;
